import { faArrowDown, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Breadcrumbs,
  Chip,
  CircularProgress,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import VoxelIconButton from '../../../../components/voxel/Buttons/VoxelIconButton';
import VoxelChip from '../../../../components/voxel/Chips/VoxelChip';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionButton from '../../../../components/voxel/Dialogs/VoxelDialogActionButton';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelCheckbox from '../../../../components/voxel/Forms/VoxelCheckbox';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import VoxelDivider from '../../../../components/voxel/VoxelDivider';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';

export interface TagBasedPlanDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  setProcessingOpen: Dispatch<SetStateAction<boolean>>;
  reload: () => Promise<any>;
  project: ProjectGq;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    breadcrumbs: { lineHeight: 2 },
    hidden: {
      opacity: 0,
    },
  })
);

interface FormState {
  serpBasedUrl: boolean;
  tagGroups: TagGroupState[];
}

interface TagGroupState {
  id: number;
  selected: boolean;
}

const TagBasedPlanDialog: React.FC<TagBasedPlanDialogProps> = ({
  openDialog,
  setOpenDialog,
  project,
  reload,
  setProcessingOpen,
}) => {
  // Form state
  const initialState = {
    serpBasedUrl: false,
    tagGroups: project.tagGroups.map((tg, index) => ({
      id: tg.id,
      selected: index <= 5,
    })),
  };
  const [formState, setFormState] = useState<FormState>(initialState);
  const [updating, setUpdating] = useState(false);

  // Close dialog
  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  // Create plan
  const initiateTagBasedPlanMutation =
    projectsApiService.mutations.contentPlan.useInitiateTagBasedPlan();

  const handleCreatePlan = useCallback(() => {
    if (updating) return;
    setUpdating(true);
    initiateTagBasedPlanMutation({
      orderedTagGroupIds: formState.tagGroups.filter((tg) => tg.selected).map((tg) => tg.id),
      serpBasedUrl: false,
      projectId: project.id,
    })
      .then(() => reload())
      .then(() => handleCloseDialog())
      .finally(() => {
        setUpdating(false);
        setProcessingOpen(true);
        setFormState(initialState);
      });
  }, [formState, updating, project.id]);

  // Get data
  const clientDomain = useMemo(
    () => project.domains.find((domain) => domain.type === DomainTypeGq.PROJECT),
    [project.domains]
  );
  const hostName = clientDomain?.hostName;

  const tagGroups = useMemo(
    () =>
      formState.tagGroups.map((tgState) => ({
        state: tgState,
        tagGroup: project.tagGroups.find((tg) => tg.id === tgState.id),
      })),
    [project.tagGroups, formState.tagGroups]
  );

  // Form actions
  const handleToggleSelected = (tagGroupId: number) => {
    setFormState((prev) => ({
      ...prev,
      tagGroups: prev.tagGroups.map((tagGroup) =>
        tagGroup.id === tagGroupId ? { ...tagGroup, selected: !tagGroup.selected } : tagGroup
      ),
    }));
  };

  const handleChangeOrder = (tagGroupId: number, positionChange: number) => {
    setFormState((prev) => {
      const next = { ...prev };
      next.tagGroups = [...next.tagGroups];

      const tagGroupIndex = next.tagGroups.findIndex((tg) => tg.id === tagGroupId);
      if (tagGroupId <= -1) return next;

      const tagGroup = next.tagGroups.splice(tagGroupIndex, 1)[0]; // Splice removes the tag group from the array
      next.tagGroups.splice(tagGroupIndex + positionChange, 0, tagGroup); // Splice then inserts the tag group at the correct position

      return next;
    });
  };

  const selectedTagGroups = useMemo(() => tagGroups.filter((tg) => tg.state.selected), [tagGroups]);

  // Styling
  const theme = useTheme();
  const classes = useClasses(theme);

  return (
    <VoxelDialog
      title={'Tag based plan - select & combine tag groups'}
      open={openDialog}
      onClose={handleCloseDialog}
    >
      <VoxelDialogContent classMinWidth="min-w-[800px]">
        <div className="-mt-2 mb-6">
          <VoxelText
            className=" whitespace-pre-line text-[13px]"
            colorClassName="text-voxel-blue-600"
          >
            Example: By combining the tag groups "material" and "color" like "/material/color...",
            all keywords having both the "leather" and "cognac" tags will be mapped to a url with
            the subdirectory "/leather/cognac...".
          </VoxelText>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            {/* Tag URL illustration */}
            {formState.serpBasedUrl === false && (
              <Grid item>
                <Breadcrumbs className={classes.breadcrumbs} maxItems={30}>
                  {!!hostName ? <VoxelText>{hostName}</VoxelText> : ''}
                  {selectedTagGroups.map((tagGroup, index) => (
                    <VoxelChip
                      key={tagGroup.tagGroup.id}
                      title={tagGroup.tagGroup.name}
                      color="info"
                    />
                  ))}
                  {/* This last one adds a slash at the end, and also makes sure the height is consistent even if all chips are removed */}
                  <Chip className={classes.hidden} />
                </Breadcrumbs>
              </Grid>
            )}
          </div>
          <VoxelDivider />
          {/* Tag group selection */}
          <div>
            <div className="flex flex-col gap-2">
              {tagGroups.map((tagGroup, index) => (
                <div
                  key={tagGroup.tagGroup.id}
                  className="p-2 flex flex-row items-center flex-nowrap justify-between"
                >
                  <div className="flex flex-row items-center flex-nowrap gap-3">
                    <VoxelCheckbox
                      color="slate"
                      checked={tagGroup.state.selected}
                      onChange={() => handleToggleSelected(tagGroup.tagGroup.id)}
                    />
                    <div>
                      <VoxelText variant="bread-normal" className="font-semibold">
                        {tagGroup.tagGroup.name}
                      </VoxelText>
                      <VoxelText variant="table">
                        {tagGroup.tagGroup.tags.slice(0, 4).map((tag) => ' ' + tag.name) +
                          (tagGroup.tagGroup.tags.length > 5
                            ? ' +' +
                            tagGroup.tagGroup.tags.slice(4, tagGroup.tagGroup.tags.length).length
                            : '')}
                      </VoxelText>
                    </div>
                  </div>
                  <div className="flex flex-row gap-2">
                    <VoxelIconButton
                      size={'sm'}
                      disabled={index === 0}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChangeOrder(tagGroup.tagGroup.id, -1);
                      }}
                      icon={<FontAwesomeIcon icon={faArrowUp} />}
                    />
                    <VoxelIconButton
                      size={'sm'}
                      disabled={index === tagGroups.length - 1}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleChangeOrder(tagGroup.tagGroup.id, 1);
                      }}
                      icon={<FontAwesomeIcon icon={faArrowDown} />}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={handleCloseDialog} />
        <VoxelDialogActionSubmit
          color="blue"
          title={updating ? <CircularProgress size={18} /> : 'Create plan'}
          disabled={updating || !formState.tagGroups.some((tg) => tg.selected)}
          onClick={handleCreatePlan}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default TagBasedPlanDialog;
